import React from 'react'
import { useTranslation } from 'react-i18next'

import { useResidentialAddressLookup } from '@pages/Signup/hooks/useResidentialAddressLookup'

import { Span } from '@ui/Typography'
import { ListItem } from '@ui/components/List'
import { Popover, PopoverCard, PopoverList } from '@ui/components/Popover'
import { Loader } from '@ui/components/Spinner/Loader'
import { SearchIcon } from '@ui/icons/Search.icon'

import { useWindowResize } from '@utils/domUtils'

import { createFormCheckBoxField } from '@global/formField/FormCheckBoxField'
import { createFormField } from '@global/formField/FormField'

import { FieldWrapper } from '@shared/components/FieldWrapper'

import type { PersonalDetailsStep4FormValues } from './PersonalDetailsStep4Form'

const FormField = createFormField<PersonalDetailsStep4FormValues>()
const FormCheckBoxField = createFormCheckBoxField<PersonalDetailsStep4FormValues>()

interface ResidentialAddressFieldsProps {
  countryId?: string
}

export const ResidentialAddressFields: React.FC<ResidentialAddressFieldsProps> = ({
  countryId,
}) => {
  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const {
    isLookup,
    isStreetHouseNumberNotAvailable,
    suggestions,
    canShowManualOption,
    canShowDropdown,
    showLookupLoader,
    setIsDropdownOpen,
    openMobileLookupModal,
    handleAddressValueChange,
    handleSelectSuggestion,
    handleManualEntry,
    handleStreetAvailabilityChange,
  } = useResidentialAddressLookup({ countryId })
  const showAddressDetails = !isLookup

  return (
    <FieldWrapper>
      <Popover
        direction='top'
        disabled
        isOpen={canShowDropdown}
        isWidthFixed
        setIsOpen={setIsDropdownOpen}
        content={
          <PopoverCard>
            <PopoverList variant='compact'>
              {suggestions.map((suggestion) => (
                <ListItem
                  key={suggestion.placeId}
                  onClick={() => handleSelectSuggestion(suggestion)}
                  subtitle={suggestion.secondaryText}
                >
                  {suggestion.mainText || suggestion.text}
                </ListItem>
              ))}
              {canShowManualOption && (
                <ListItem onClick={handleManualEntry}>
                  {t("Sign up.Can't find your address?")}{' '}
                  <Span semibold color='text-purple-high-em'>
                    {t('Sign up.Enter manually')}
                  </Span>
                </ListItem>
              )}
            </PopoverList>
          </PopoverCard>
        }
      >
        <FormField
          type='text'
          required
          name='address'
          showLabel
          autoComplete='off'
          leftIcon={isLookup ? SearchIcon : undefined}
          readOnly={isMobile && isLookup}
          label={isLookup ? t('Sign up.Search or enter your address') : t('Sign up.Address Line 1')}
          onClick={isMobile && isLookup ? openMobileLookupModal : undefined}
          onChange={(e) => handleAddressValueChange(e.target.value)}
          onKeyDown={({ key }) => {
            if (key === 'Escape') {
              setIsDropdownOpen(false)
            }
          }}
          renderRightIconJSX={
            showLookupLoader ? <Loader size={20} color='text-high-em' /> : undefined
          }
        />
      </Popover>

      {showAddressDetails && (
        <>
          <FormField
            type='text'
            required
            name='streetHouseNumber'
            showLabel
            disabled={isStreetHouseNumberNotAvailable}
            label={t('Sign up.Street/House. Number')}
          />

          <FormCheckBoxField
            name='streetHouseNumberNotAvailable'
            onChange={handleStreetAvailabilityChange}
          >
            {t('Sign up.Not Available')}
          </FormCheckBoxField>

          <FormField
            type='text'
            required
            name='primaryAddressCity'
            showLabel
            label={t('Sign up.City')}
          />

          <FormField
            type='text'
            required
            name='primaryAddressState'
            showLabel
            label={t('Sign up.State')}
          />

          <FormField
            type='text'
            required
            name='primaryAddressPostalCode'
            showLabel
            label={t('Sign up.ZIP / Postal code')}
          />
        </>
      )}
    </FieldWrapper>
  )
}
